import cn from "@/lib/cn";
import React from "react";

export interface ContentContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: "div" | "section";
  theme?: "light" | "dark" | "theme1-base" | "neutral-base" | "transparent";
  spacing?:
    | "none"
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "product-cards"
    | "product-cards-no-desktop-padding";
  mobileFullWidth?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Responsive Content Container that matches the Grid container specs in `docs/design-guide.md`.
 *
 * Inner vs outer cap:
 * - Inner content cap (design): Mobile 25rem, Tablet 60rem, Desktop 88rem.
 * - Outer container cap (implementation): includes horizontal padding applied on this element
 *   so the inner content area equals the design cap.
 *
 * Padding and variables:
 * - Horizontal padding: `px-6 md:px-8 xl:px-16` (adds 3rem/4rem/8rem total width).
 * - Outer max-widths are set via CSS vars in `src/styles/globals.css`:
 *   `--container-mobile: 28rem`, `--container-tablet: 64rem`, `--container-desktop: 96rem`.
 *   These equal inner caps + padding so the readable area is 25/60/88rem.
 *
 * Note: Columns/gutters within the container should be handled inside as needed.
 */
const ContentContainer: React.FC<ContentContainerProps> = ({
  as: Tag = "section",
  theme,
  spacing = "md",
  mobileFullWidth = false,
  ref,
  ...props
}) => {
  const themeClasses = {
    light: "bg-white text-black light",
    dark: "bg-dark-secondary text-inverse-white dark",
    "theme1-base": "bg-light text-black light",
    "neutral-base": "bg-white text-black",
    transparent: "bg-transparent",
  };

  const spacingClasses = {
    none: "",
    sm: "py-8 md:py-12",
    md: "py-8",
    lg: "py-16 md:py-20",
    xl: "py-20 md:py-24",
    "product-cards": "py-8",
    "product-cards-no-desktop-padding": "pt-10 md:pt-16 xl:pt-0 pb-16 xl:pb-0",
  };

  return (
    <Tag
      {...props}
      ref={ref}
      className={cn(
        // Center and full width
        "mx-auto w-full",
        // Max content width caps per breakpoint
        mobileFullWidth ? "content-container-mobile-full" : "content-container",
        // Theme classes
        theme && themeClasses[theme],
        // Spacing classes
        spacingClasses[spacing],
        props?.className
      )}
    >
      {props.children}
    </Tag>
  );
};

export default ContentContainer;
